import React, { useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18n-lite'

import { NavigationSlider, useIsMobile } from '@yes.technology/react-toolkit'

import { useSiteState } from 'siteState/shared'

import { FilterFieldsDisplayOptions } from '../../FilterFields'

import {
  CenterWrapper,
  FirstElement,
  StyledCollapsibleCard,
  StyledFilterButton
} from './GroupControls.styles'

export type GroupControlsProp = {
  elementUuid: string
  sectionPosition: number
  sectionsLength: number
  setSectionPosition: (section: number) => void
  groupPosition: number
  groupsLength: number
  setGroupPosition: (group: number) => void
  handleClearCurrentGroup: () => void
  navigationSliderBackgroundColor?: string
  navigationSliderColor?: string
  enableComposition?: boolean
  children: React.ReactNode
  shouldHideFilters: boolean
}

export default function GroupControls({
  sectionsLength,
  sectionPosition,
  setSectionPosition,
  groupPosition,
  groupsLength,
  handleClearCurrentGroup,
  setGroupPosition,
  navigationSliderBackgroundColor,
  navigationSliderColor,
  children,
  shouldHideFilters,
  elementUuid,
  enableComposition
}: GroupControlsProp) {
  const { t } = useTranslation()

  const collapsibleCardRef = useRef({
    onOpen: () => ({}),
    onClose: () => ({}),
    onToggle: () => ({})
  })

  const isMobile = useIsMobile()

  const areaLineTitle = (
    <FirstElement color={navigationSliderColor}>
      {t('report.filter-area.filter-head.filter-group')}
    </FirstElement>
  )

  const leftHeaderElement = isMobile ? undefined : areaLineTitle

  const centerHeaderElement = (
    <CenterWrapper>
      <NavigationSlider
        heightClass='small'
        currentPosition={sectionPosition}
        first={1}
        total={sectionsLength}
        afterClick={setSectionPosition}
        textForCurrent={t('filter-fields.section')}
        hierarchyLevel='level_3'
        backgroundColor={navigationSliderBackgroundColor}
        color={navigationSliderColor}
      />
      {isMobile && (
        <>
          <NavigationSlider
            heightClass='small'
            hierarchyLevel='level_3'
            currentPosition={groupPosition}
            first={1}
            total={groupsLength}
            afterClick={setGroupPosition}
            textForCurrent='Grupo'
            arrowOrientationButtons='diagonalArrows'
            backgroundColor={navigationSliderBackgroundColor}
            color={navigationSliderColor}
          />
          <StyledFilterButton
            heightClass='regular'
            className='button-limpar'
            onClick={handleClearCurrentGroup}
            aria-label='Limpar grupo'
          >
            {t('filter-fields.clean')}
          </StyledFilterButton>
        </>
      )}
    </CenterWrapper>
  )

  const rightHeaderElement =
    isMobile || shouldHideFilters || !enableComposition ? undefined : (
      <>
        <NavigationSlider
          heightClass='small'
          hierarchyLevel='level_3'
          currentPosition={groupPosition}
          first={1}
          total={groupsLength}
          afterClick={setGroupPosition}
          textForCurrent='Grupo'
          arrowOrientationButtons='diagonalArrows'
          backgroundColor={navigationSliderBackgroundColor}
          color={navigationSliderColor}
        />
        <StyledFilterButton
          heightClass='regular'
          className='button-limpar'
          onClick={handleClearCurrentGroup}
          aria-label='Limpar grupo'
        >
          {t('filter-fields.clean')}
        </StyledFilterButton>
      </>
    )

  const [displayOptions] = useSiteState<FilterFieldsDisplayOptions>(
    'filter-display-options'
  )

  const displayOptionsElement = displayOptions?.[elementUuid]

  useEffect(() => {
    if (!shouldHideFilters) {
      collapsibleCardRef?.current?.onOpen()
      return
    }

    collapsibleCardRef?.current?.onClose()
  }, [shouldHideFilters])

  if (displayOptionsElement?.hideFieldsCollapsibleContainer)
    return <>{children}</>

  return (
    <StyledCollapsibleCard
      data-testid='group-controls'
      leftHeaderElement={leftHeaderElement}
      centerHeaderElement={centerHeaderElement}
      rightHeaderElement={rightHeaderElement}
      heightClass='small'
      headerColor='blue'
      alignSecondPosition='center'
      ref={collapsibleCardRef}
      defaultOpenState
      wrapRightAreaElement
      backgroundColor={navigationSliderBackgroundColor}
      color={navigationSliderColor}
    >
      {children}
    </StyledCollapsibleCard>
  )
}
